import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeSlideUp, useSnapVariants } from '../../lib/motion';

type ToastVariant = 'default' | 'destructive' | 'success';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  onClose?: () => void;
  action?: ToastAction;
}

// 토스트 메시지 전역 상태
let addToast: (toast: ToastProps & { id: string }) => void = () => {}; // 기본 빈 함수
let removeToastById: (id: string) => void = () => {}; // 기본 빈 함수

export interface ToastHandle {
  id: string;
  dismiss: () => void;
}

/**
 * 토스트 알림 생성 함수
 * @param props 토스트 속성
 * @returns ToastHandle — id와 dismiss() 함수 (기존 호출자는 반환값 무시 가능)
 */
export const toast = (props: ToastProps): ToastHandle => {
  const id = Math.random().toString(36).slice(2);
  // 등록된 addToast 사용
  addToast({
    ...props,
    id,
    duration: props.duration || 3000, // 기본 3초
    variant: props.variant || 'default'
  });
  return {
    id,
    dismiss: () => removeToastById(id),
  };
};

/**
 * 토스트 컨테이너 컴포넌트
 * - 토스트 메시지를 관리하고 표시
 */
export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);
  const snapFadeSlideUp = useSnapVariants(fadeSlideUp);

  // 토스트 추가 함수
  const handleAddToast = (toast: ToastProps & { id: string }) => {
    setToasts(prevToasts => [...prevToasts, toast]);

    // 자동 제거 타이머 설정
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        handleRemoveToast(toast.id);
      }, toast.duration);
    }
  };

  // 토스트 제거 함수
  const handleRemoveToast = (id: string) => {
    setToasts(prevToasts => prevToasts.filter(t => t.id !== id));
  };

  // 전역 함수 등록
  useEffect(() => {
    addToast = handleAddToast;
    removeToastById = handleRemoveToast;

    return () => {
      addToast = () => {};
      removeToastById = () => {};
    };
  }, []);
  
  // 포탈 생성 및 렌더링
  return createPortal(
    <div
      role="region"
      aria-live="polite"
      aria-label="알림"
      className="fixed right-4 left-4 md:left-auto md:right-4 z-50 flex flex-col gap-2 bottom-[calc(var(--bottom-nav-height,64px)+16px)] md:bottom-4 max-w-md w-auto md:w-full md:max-w-md pointer-events-none"
    >
      <AnimatePresence initial={false}>
        {toasts.map(toast => (
          <motion.div
            key={toast.id}
            variants={snapFadeSlideUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="status"
            className={`
              rounded-lg shadow-lg p-4
              pointer-events-auto
              ${toast.variant === 'destructive' ? 'bg-red-600 dark:bg-red-700 text-white' :
                toast.variant === 'success' ? 'bg-green-600 dark:bg-green-700 text-white' :
                'bg-gray-800 dark:bg-gray-900 text-white'}
            `}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{toast.title}</h3>
                {toast.description && (
                  <p className="text-sm mt-1">{toast.description}</p>
                )}
                {toast.action && (
                  <button
                    className="mt-2 text-sm font-medium underline opacity-90 hover:opacity-100 transition-opacity"
                    onClick={() => {
                      toast.action!.onClick();
                      handleRemoveToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button
                className="text-white hover:opacity-80 transition-opacity"
                onClick={() => handleRemoveToast(toast.id)}
                aria-label="Close notification"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default ToastContainer; 